import { asList } from '../lib/api';
import { useState, useEffect } from 'react';
import { languageApi, type LanguageOption } from '../lib/languageApi';
import { useI18n } from '../context/I18nContext';

/**
 * A compact language picker. Sets the user's PREFERRED LANGUAGE — used for BOTH the UI language (this
 * app's own labels, translated live) and the language their notifications (SMS, email, in-app) are
 * translated into. Shown with each language in its own script (endonym) so a parent recognises their
 * own language without reading English.
 */
export function LanguagePicker({ compact = false }: { compact?: boolean }) {
  const [options, setOptions] = useState<LanguageOption[]>([]);
  const { lang, setLang } = useI18n();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    languageApi.options().then((r) => setOptions(asList(r))).catch(() => setOptions([]));
  }, []);

  const change = async (next: string) => {
    setSaving(true); setSaved(false);
    try { await setLang(next); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };

  if (options.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {!compact && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Language</span>}
      <select
        className="input"
        value={lang}
        onChange={(e) => change(e.target.value)}
        disabled={saving}
        style={{ width: 'auto', padding: '4px 8px', fontSize: 13 }}
        aria-label="Language"
      >
        {options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
      </select>
      {saved && <span style={{ fontSize: 11, color: 'var(--paid, #2e7d32)' }}>✓</span>}
    </div>
  );
}
